import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

// Types
export interface VariantAttribute {
  id: string;
  organization_id: string;
  name: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
  values?: VariantAttributeValue[];
}

export interface VariantAttributeValue {
  id: string;
  attribute_id: string;
  value: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProductVariant {
  id: string;
  product_id: string;
  name: string;
  sku: string | null;
  barcode: string | null;
  price_adjustment: number;
  cost: number | null;
  is_active: boolean;
  sort_order: number;
  image_url: string | null;
  inventory_quantity: number;
  created_at: string;
  updated_at: string;
  attribute_values?: VariantAttributeValue[];
}

export interface ProductVariantAttribute {
  id: string;
  product_id: string;
  attribute_id: string;
  sort_order: number;
  attribute?: VariantAttribute;
}

// Hook for managing organization-level variant attributes
export function useVariantAttributes() {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const organizationId = organization?.id;

  // Fetch all attributes for the organization
  const {
    data: attributes,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['variant-attributes', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .from('variant_attributes')
        .select(`
          *,
          values:variant_attribute_values(*)
        `)
        .eq('organization_id', organizationId)
        .order('sort_order');

      if (error) throw error;
      return data as VariantAttribute[];
    },
    enabled: !!organizationId,
  });

  // Create a new attribute
  const createAttribute = useMutation({
    mutationFn: async ({ name, values }: { name: string; values?: string[] }) => {
      if (!organizationId) throw new Error('No organization');

      // Create the attribute
      const { data: attribute, error: attrError } = await supabase
        .from('variant_attributes')
        .insert({
          organization_id: organizationId,
          name,
          sort_order: (attributes?.length || 0),
        })
        .select()
        .single();

      if (attrError) throw attrError;

      // Create values if provided
      if (values && values.length > 0) {
        const { error: valuesError } = await supabase
          .from('variant_attribute_values')
          .insert(
            values.map((value, index) => ({
              attribute_id: attribute.id,
              value,
              sort_order: index,
            }))
          );

        if (valuesError) throw valuesError;
      }

      return attribute;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variant-attributes', organizationId] });
    },
  });

  // Update an attribute
  const updateAttribute = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { data, error } = await supabase
        .from('variant_attributes')
        .update({ name, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variant-attributes', organizationId] });
    },
  });

  // Delete an attribute
  const deleteAttribute = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('variant_attributes')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variant-attributes', organizationId] });
    },
  });

  // Add a value to an attribute
  const addAttributeValue = useMutation({
    mutationFn: async ({ attributeId, value }: { attributeId: string; value: string }) => {
      const { data, error } = await supabase
        .from('variant_attribute_values')
        .insert({
          attribute_id: attributeId,
          value,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variant-attributes', organizationId] });
    },
  });

  // Update a value
  const updateAttributeValue = useMutation({
    mutationFn: async ({ id, value }: { id: string; value: string }) => {
      const { data, error } = await supabase
        .from('variant_attribute_values')
        .update({ value, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variant-attributes', organizationId] });
    },
  });

  // Delete a value
  const deleteAttributeValue = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('variant_attribute_values')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variant-attributes', organizationId] });
    },
  });

  return {
    attributes: attributes ?? [],
    isLoading,
    error,
    refetch,
    createAttribute,
    updateAttribute,
    deleteAttribute,
    addAttributeValue,
    updateAttributeValue,
    deleteAttributeValue,
  };
}

// Hook for managing product variants
export function useProductVariants(productId: string | null) {
  const queryClient = useQueryClient();

  // Fetch variants for a product
  const {
    data: variants,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['product-variants', productId],
    queryFn: async () => {
      if (!productId) return [];

      const { data, error } = await supabase
        .from('product_variants')
        .select(`
          *,
          attribute_values:product_variant_attribute_values(
            *,
            attribute_value:variant_attribute_values(
              *,
              attribute:variant_attributes(*)
            )
          )
        `)
        .eq('product_id', productId)
        .order('sort_order');

      if (error) throw error;
      return data as ProductVariant[];
    },
    enabled: !!productId,
  });

  // Fetch attributes used by this product
  const { data: productAttributes } = useQuery({
    queryKey: ['product-variant-attributes', productId],
    queryFn: async () => {
      if (!productId) return [];

      const { data, error } = await supabase
        .from('product_variant_attributes')
        .select(`
          *,
          attribute:variant_attributes(
            *,
            values:variant_attribute_values(*)
          )
        `)
        .eq('product_id', productId)
        .order('sort_order');

      if (error) throw error;
      return data as ProductVariantAttribute[];
    },
    enabled: !!productId,
  });

  // Set attributes for a product
  const setProductAttributes = useMutation({
    mutationFn: async ({ productId, attributeIds }: { productId: string; attributeIds: string[] }) => {
      // Delete existing
      await supabase
        .from('product_variant_attributes')
        .delete()
        .eq('product_id', productId);

      // Insert new
      if (attributeIds.length > 0) {
        const { error } = await supabase
          .from('product_variant_attributes')
          .insert(
            attributeIds.map((attrId, index) => ({
              product_id: productId,
              attribute_id: attrId,
              sort_order: index,
            }))
          );

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-variant-attributes', productId] });
      queryClient.invalidateQueries({ queryKey: ['product-variants', productId] });
    },
  });

  // Create a variant
  const createVariant = useMutation({
    mutationFn: async ({
      productId,
      name,
      sku,
      barcode,
      priceAdjustment,
      cost,
      attributeValueIds,
    }: {
      productId: string;
      name: string;
      sku?: string;
      barcode?: string;
      priceAdjustment?: number;
      cost?: number;
      attributeValueIds?: string[];
    }) => {
      // Create the variant
      const { data: variant, error: varError } = await supabase
        .from('product_variants')
        .insert({
          product_id: productId,
          name,
          sku: sku || null,
          barcode: barcode || null,
          price_adjustment: priceAdjustment || 0,
          cost: cost || null,
        })
        .select()
        .single();

      if (varError) throw varError;

      // Link attribute values if provided
      if (attributeValueIds && attributeValueIds.length > 0) {
        const { error: linkError } = await supabase
          .from('product_variant_attribute_values')
          .insert(
            attributeValueIds.map(valueId => ({
              variant_id: variant.id,
              attribute_value_id: valueId,
            }))
          );

        if (linkError) throw linkError;
      }

      return variant;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-variants', productId] });
    },
  });

  // Update a variant
  const updateVariant = useMutation({
    mutationFn: async ({
      variantId,
      updates,
    }: {
      variantId: string;
      updates: Partial<{
        name: string;
        sku: string | null;
        barcode: string | null;
        price_adjustment: number;
        cost: number | null;
        is_active: boolean;
        image_url: string | null;
        inventory_quantity: number;
      }>;
    }) => {
      const { data, error } = await supabase
        .from('product_variants')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', variantId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-variants', productId] });
    },
  });

  // Delete a variant
  const deleteVariant = useMutation({
    mutationFn: async (variantId: string) => {
      const { error } = await supabase
        .from('product_variants')
        .delete()
        .eq('id', variantId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-variants', productId] });
    },
  });

  // Generate variants from attribute combinations
  const generateVariants = async (
    productId: string,
    attributeValueCombinations: { name: string; valueIds: string[] }[]
  ) => {
    for (const combo of attributeValueCombinations) {
      await createVariant.mutateAsync({
        productId,
        name: combo.name,
        attributeValueIds: combo.valueIds,
      });
    }
  };

  // Helper to generate all combinations
  const generateAttributeCombinations = (
    attributes: { name: string; values: { id: string; value: string }[] }[]
  ): { name: string; valueIds: string[] }[] => {
    if (attributes.length === 0) return [];

    const combine = (
      index: number,
      current: { name: string; valueIds: string[] }
    ): { name: string; valueIds: string[] }[] => {
      if (index >= attributes.length) {
        return [current];
      }

      const results: { name: string; valueIds: string[] }[] = [];
      const attr = attributes[index];

      for (const value of attr.values) {
        const newName = current.name ? `${current.name} / ${value.value}` : value.value;
        const newValueIds = [...current.valueIds, value.id];
        results.push(...combine(index + 1, { name: newName, valueIds: newValueIds }));
      }

      return results;
    };

    return combine(0, { name: '', valueIds: [] });
  };

  return {
    variants: variants ?? [],
    productAttributes: productAttributes ?? [],
    isLoading,
    error,
    refetch,
    setProductAttributes,
    createVariant,
    updateVariant,
    deleteVariant,
    generateVariants,
    generateAttributeCombinations,
  };
}


















